import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { trackException } from '@/core/telemetry/appInsights';
import { ApiError } from '@/models/apiError';
import { Button } from '@/shared/ui/button';

interface RootErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface RootErrorBoundaryProps {
  readonly children?: ReactNode;
}

/**
 * Root-level error boundary. Catches uncaught render errors, reports to
 * Application Insights, and presents a generic fallback UI.
 */
export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  constructor(props: RootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, _info: ErrorInfo): void {
    const correlationId = error instanceof ApiError ? error.correlationId : null;
    trackException(error, correlationId);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="flex h-screen flex-col items-center justify-center gap-4 p-8"
          role="alert"
        >
          <h1 className="text-2xl font-semibold">Ein Fehler ist aufgetreten</h1>
          <p className="text-muted-foreground">
            Bitte laden Sie die Seite neu oder kontaktieren Sie den Support.
          </p>
          <Button onClick={this.handleReset}>Zur Startseite</Button>
        </div>
      );
    }

    return this.props.children;
  }
}
